import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES } from "@/data/services";
import Reveal from "@/components/Reveal";
import HowWeWork from "@/components/HowWeWork";
import CTASection from "@/components/CTASection";
import { useEffect } from "react";

export const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) document.title = `${service.title} | Track Digital`;
  }, [slug, service]);

  if (!service) return <Navigate to="/services" replace />;

  return (
    <main data-testid={`service-page-${slug}`}>
      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[#eee]">
        <div className="td-container">
          <Reveal>
            <Link to="/services" className="inline-flex items-center gap-2 text-[#8a8a8a] text-sm font-medium uppercase tracking-widest mb-6 hover:text-[#7C6CF0] transition-colors">
              Services
            </Link>
            <h2 data-testid="service-title" className="font-head text-2xl md:text-3xl font-semibold text-[#7C6CF0] mb-3">
              {service.title}
            </h2>
            {service.positioning && (
              <p className="text-[#6b6b6b] text-sm uppercase tracking-widest mb-6">{service.positioning}</p>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] max-w-4xl">
              {service.hero}
            </h1>
            <p className="td-measure text-lg text-[#4a4a4a] leading-relaxed mt-8">{service.intro}</p>
            <div className="mt-10">
              <Link to="/contact" data-testid="service-hero-cta" className="td-btn td-btn-primary">
                Book a Discovery Call <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Choose Your Entry Point (compact comparison, opt-in per service) */}
      {service.entryComparison?.length > 0 && (
        <section className="py-16 md:py-20" data-testid="entry-comparison">
          <div className="td-container">
            <Reveal className="mb-10 max-w-2xl">
              <span className="td-label block mb-4">Choose Your Entry Point</span>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight">
                {service.entryComparison[0]?.headline || "Where should you start?"}
              </h2>
              <p className="text-[#6b6b6b] text-base leading-relaxed mt-4">
                {service.entryComparison[0]?.subhead ||
                  "A quick at-a-glance view to help you pick the right starting point. Full details in Ways to Work below."}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {service.entryComparison.slice(1).map((row, i) => {
                const tints = ["#DDF2E7", "#FFF1C9", "#ECE9FF"];
                return (
                  <Reveal key={row.name} delay={i * 80}>
                    <div
                      data-testid={`entry-card-${i}`}
                      className="rounded-3xl p-7 md:p-8 h-full flex flex-col"
                      style={{ background: tints[i % tints.length] }}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="font-head text-lg font-semibold text-black">{row.name}</h3>
                        <span className="font-head text-2xl font-semibold text-black whitespace-nowrap">
                          {row.price}
                        </span>
                      </div>
                      <dl className="mt-6 space-y-4 flex-1">
                        <div>
                          <dt className="td-label mb-1.5">Best for</dt>
                          <dd className="text-sm text-[#3a3a44] leading-relaxed">{row.bestFor}</dd>
                        </div>
                        <div>
                          <dt className="td-label mb-1.5">Timeline</dt>
                          <dd className="text-sm text-[#3a3a44]">{row.timeline}</dd>
                        </div>
                        <div>
                          <dt className="td-label mb-1.5">You leave with</dt>
                          <dd className="text-sm text-[#3a3a44] leading-relaxed">{row.deliverable}</dd>
                        </div>
                      </dl>
                      <a
                        href="#packages"
                        className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-[#7C6CF0] hover:text-[#5F4FD6] transition-colors"
                      >
                        See what is included <ArrowRight size={14} />
                      </a>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Problem + Approach */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="td-container grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <span className="td-label block mb-5">The Problem</span>
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 leading-tight">Sound familiar?</h2>
            <ul className="space-y-4">
              {service.problem.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#4a4a4a] text-lg">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#7C6CF0] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <span className="td-label block mb-5">The Track Approach</span>
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 leading-tight">How we solve it.</h2>
            <p className="text-[#4a4a4a] text-lg leading-relaxed">{service.approach}</p>
          </Reveal>
        </div>
      </section>

      {/* What is included */}
      <section className="py-20 md:py-28">
        <div className="td-container">
          <Reveal>
            <span className="td-label block mb-5">What Is Included</span>
            <h2 className="text-3xl md:text-5xl font-semibold mb-14 max-w-2xl leading-[1.1]">
              Everything, handled properly.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {service.included.map((item, i) => (
              <Reveal key={item} delay={(i % 3) * 60}>
                <div className="flex items-start gap-3 py-4 border-b border-[#eee]">
                  <Check size={18} className="text-[#7C6CF0] mt-1 shrink-0" />
                  <span className="text-[#2b2b2b] text-[15px]">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who it is for + Outcomes */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="td-container grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <span className="td-label block mb-5">Who It Is For</span>
            <ul className="space-y-5 mt-4">
              {service.whoFor.map((w) => (
                <li key={w} className="text-xl md:text-2xl font-head font-medium text-black leading-snug">
                  {w}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <span className="td-label block mb-5">Outcomes</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {service.outcomes.map((o) => (
                <div key={o} className="bg-white rounded-2xl border border-[#eee] p-5 text-[#2b2b2b] text-[15px]">
                  {o}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Packages */}
      {service.packages?.length > 0 && (
        <section id="packages" className="py-20 md:py-28 scroll-mt-32">
          <div className="td-container">
            <Reveal>
              <span className="td-label block mb-5">Ways To Work</span>
              <h2 className="text-3xl md:text-5xl font-semibold mb-6 max-w-2xl leading-[1.1]">
                Shaped around your goals and capacity.
              </h2>
              {service.packages.some((p) => p.price === "POA") && (
                <p className="text-[#6b6b6b] text-[15px] leading-relaxed max-w-2xl mb-14">
                  <span className="font-semibold text-[#7C6CF0]">POA - Price on Application.</span> These
                  projects vary a lot, so rather than a fixed figure we scope the work to you and give a clear,
                  tailored quote after a short discovery call.
                </p>
              )}
            </Reveal>
            <div className={`grid grid-cols-1 md:grid-cols-2 ${service.packages.length >= 3 ? "lg:grid-cols-3" : ""} gap-6`}>
              {service.packages.map((pkg, i) => (
                <Reveal key={pkg.name} delay={i * 80}>
                  <div
                    data-testid={`package-${i}`}
                    className={`rounded-3xl p-8 md:p-9 h-full flex flex-col ${
                      pkg.highlighted
                        ? "bg-[#ECE9FF] border-2 border-[#7C6CF0]"
                        : "bg-white border border-[#e5e5e5]"
                    }`}
                  >
                    <h3 className="font-head text-xl font-semibold text-black">{pkg.name}</h3>
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="font-head text-4xl font-semibold text-black">{pkg.price}</span>
                      {pkg.period && <span className="text-[#8a8a8a]">{pkg.period}</span>}
                    </div>
                    <p className="mt-3 text-sm font-medium text-[#7C6CF0]">{pkg.subtitle}</p>
                    {pkg.note && <p className="mt-2 text-sm text-[#8a8a8a]">{pkg.note}</p>}
                    {i > 0 && !pkg.standalone && !service.packages[i - 1].standalone && (
                      <p className="mt-5 text-sm font-medium text-black">
                        Everything in {service.packages[i - 1].name}, plus:
                      </p>
                    )}
                    <ul className="mt-6 space-y-3 flex-1">
                      {pkg.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <Check size={16} className="mt-0.5 shrink-0 text-[#7C6CF0]" />
                          <span className="text-[#4a4a4a]">{f}</span>
                        </li>
                      ))}
                    </ul>
                    {pkg.footnote && (
                      <p className="mt-4 text-xs text-[#8a8a8a] italic">{pkg.footnote}</p>
                    )}
                    <Link
                      to="/contact"
                      className={`td-btn mt-8 w-full ${pkg.highlighted ? "td-btn-primary" : "td-btn-outline"}`}
                    >
                      Enquire
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Writing & SEO */}
      {service.blogWriting && (
        <section id="blog-writing" className="pb-20 md:pb-28 scroll-mt-32" data-testid="blog-writing-section">
          <div className="td-container">
            <Reveal className="max-w-2xl mb-12">
              <span className="td-label block mb-5">{service.blogWriting.label}</span>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-5">{service.blogWriting.heading}</h2>
              <p className="text-[#4a4a55] text-lg leading-relaxed">{service.blogWriting.intro}</p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {service.blogWriting.options.map((opt, i) => (
                <Reveal key={opt.name} delay={i * 80}>
                  <div
                    data-testid={`blog-option-${i}`}
                    className="rounded-3xl p-8 md:p-9 h-full flex flex-col"
                    style={{ background: i === 1 ? "#ECE9FF" : "#FFF1C9" }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-head text-xl font-semibold text-black">{opt.name}</h3>
                      <span className="text-xs font-medium text-[#4a4a4a] bg-white/70 rounded-full px-3 py-1 whitespace-nowrap">{opt.meta}</span>
                    </div>
                    <p className="font-head text-4xl font-semibold text-black mt-5">{opt.price}</p>
                    <p className="mt-3 text-[#4a4a55]">{opt.subtitle}</p>
                    {opt.idealFor && (
                      <div className="mt-6 flex-1">
                        <p className="td-label mb-3">Ideal for</p>
                        <div className="flex flex-wrap gap-2">
                          {opt.idealFor.map((x) => (
                            <span key={x} className="rounded-full bg-white/70 px-3 py-1.5 text-sm text-[#4a4a4a]">{x}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {opt.includes && (
                      <ul className="mt-6 space-y-2.5 flex-1">
                        {opt.includes.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm">
                            <Check size={16} className="mt-0.5 shrink-0 text-[#7C6CF0]" />
                            <span className="text-[#3a3a44]">{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to="/contact" className="td-btn td-btn-primary mt-8 w-full">Enquire</Link>
                  </div>
                </Reveal>
              ))}
            </div>

            {service.blogWriting.monthlyPackages && (
              <div className="mt-16" data-testid="monthly-content-packages">
                <Reveal className="max-w-2xl mb-8">
                  <span className="td-label block mb-4">{service.blogWriting.monthlyPackages.label}</span>
                  <h3 className="text-2xl md:text-3xl font-semibold leading-tight mb-3">
                    {service.blogWriting.monthlyPackages.heading}
                  </h3>
                  {service.blogWriting.monthlyPackages.note && (
                    <p className="text-[#6b6b6b] text-base leading-relaxed">
                      {service.blogWriting.monthlyPackages.note}
                    </p>
                  )}
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {service.blogWriting.monthlyPackages.options.map((mp, i) => (
                    <Reveal key={mp.name} delay={i * 80}>
                      <div
                        data-testid={`monthly-package-${i}`}
                        className="rounded-3xl p-7 md:p-8 h-full flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                        style={{ background: i === 0 ? "#DDF2E7" : "#E3EEFF" }}
                      >
                        <div>
                          <h4 className="font-head text-lg font-semibold text-black">{mp.name}</h4>
                          <p className="mt-2 text-sm text-[#4a4a55]">Ongoing SEO-optimised blogs written on rhythm.</p>
                        </div>
                        <div className="flex items-baseline gap-1 whitespace-nowrap">
                          <span className="font-head text-3xl font-semibold text-black">{mp.price}</span>
                          {mp.period && <span className="text-[#8a8a8a] text-sm">{mp.period}</span>}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/contact" className="td-btn td-btn-outline">Enquire about monthly blogs</Link>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Add-ons */}
      {service.addons?.length > 0 && (
        <section className="pb-20 md:pb-28">
          <div className="td-container">
            <Reveal>
              <span className="td-label block mb-8">Add-ons</span>
              <div className="flex flex-wrap gap-3">
                {service.addons.map((a) => (
                  <span key={a} className="rounded-full border border-[#d3d3d3] px-5 py-2.5 text-sm text-[#4a4a4a]">
                    {a}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <HowWeWork
        buildCopy={service.howWeWork?.buildCopy}
        supportCopy={service.howWeWork?.supportCopy}
      />

      {/* FAQ */}
      {service.faq?.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="td-container max-w-3xl">
            <Reveal>
              <span className="td-label block mb-5">FAQ</span>
              <h2 className="text-3xl md:text-4xl font-semibold mb-10">Practical questions.</h2>
            </Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
              {service.faq.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#eee]">
                  <AccordionTrigger className="text-left font-head text-lg font-medium hover:no-underline py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4a4a4a] text-base leading-relaxed pb-6">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Frequently Combined With */}
      {service.combinedWith?.length > 0 && (
        <section className="py-20 md:py-24 bg-[#fafafa]" data-testid="combined-with-section">
          <div className="td-container">
            <Reveal className="max-w-2xl mb-10">
              <span className="td-label block mb-5">Frequently Combined With</span>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight">
                Where this service fits next.
              </h2>
              <p className="text-[#6b6b6b] text-base leading-relaxed mt-4">
                {service.combinedWithIntro || `Most clients pair ${service.title.toLowerCase()} with one or two of the below. Together they turn direction into consistent, joined-up marketing.`}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {service.combinedWith.map((c, i) => {
                const tints = ["#DDF2E7", "#E3EEFF", "#ECE9FF"];
                const to = c.hash ? `/services/${c.slug}#${c.hash}` : `/services/${c.slug}`;
                return (
                  <Reveal key={`${c.slug}-${c.label}`} delay={i * 70}>
                    <Link
                      to={to}
                      data-testid={`combined-with-${i}`}
                      className="td-frame-card group block rounded-3xl p-7 h-full"
                      style={{ background: tints[i % tints.length], "--frame": "#7C6CF0" }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="font-head text-lg md:text-xl font-semibold text-black leading-tight">
                          {c.label}
                        </h3>
                        <ArrowUpRight size={20} className="text-black/30 group-hover:text-black/70 transition-colors shrink-0 mt-1" />
                      </div>
                      <p className="mt-4 text-sm text-[#4a4a4a]">Explore how it complements {service.title.toLowerCase()}.</p>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CTASection
        label="Next Step"
        heading={service.cta?.heading || "Let us bring clarity to this part of your marketing."}
        copy={service.cta?.copy || "Book a discovery call and we will talk through where you are, what will help, and the clearest way forward."}
      />

      <div className="td-container pb-20">
        <Link to="/services" className="inline-flex items-center gap-2 text-[#7C6CF0] font-medium hover:text-[#5F4FD6] transition-colors">
          <ArrowUpRight size={18} /> Explore all services
        </Link>
      </div>
    </main>
  );
};

export default ServicePage;
